/**
 * Limits the dashboard and the API must agree on.
 *
 * Shared rather than duplicated because both halves enforce them: the web
 * form caps its input, and the service rejects anything past the cap.
 */

/**
 * The better-auth api-key plugin's own `maximumNameLength` default, which we
 * do not override. Longer names are rejected server-side while the key is
 * being minted, so inputs are clamped to it instead.
 */
export const MAX_API_KEY_NAME_LENGTH = 32

/**
 * Enabled keys one API connection may hold. Counted on enabled keys only, so
 * a revoked key never consumes a slot and a connection at the cap can still
 * rotate. The plugin has no `maximumKeys` option, so we enforce this.
 */
export const MAX_API_KEYS_PER_STORE = 5

/**
 * Events a partner can receive on a webhook endpoint.
 *
 * Strings rather than a database enum so adding an event is a deploy, not a
 * migration. Consumers must ignore types they do not recognise, and stored
 * subscriptions containing a retired type must not break delivery of the rest.
 */
export const PUBLIC_API_WEBHOOK_EVENT_TYPES = [
  "order.created",
  "order.canceled",
  "order.onHold",
  "order.onHoldReleased",
  "order.shipmentCreated",
  "order.shipmentReturned",
  "order.completed",
  "product.updated",
  "product.published",
  "product.unpublished",
] as const

export type PublicApiWebhookEventType =
  (typeof PUBLIC_API_WEBHOOK_EVENT_TYPES)[number]

/**
 * Why an order is held, as a partner sees it.
 *
 * Deliberately coarser than the internal `OnHoldReason`, which splits the
 * same cause across the FBA, bulk and standard flows: a partner can only act
 * on what the cause asks them to do, and internal routing detail is not
 * theirs to depend on. Each value names an action — top up stock, settle
 * payment, fix the label — except `internal_review`, which means Supliful is
 * handling it and the partner waits.
 */
export const PUBLIC_API_ON_HOLD_REASONS = [
  "out_of_stock",
  "payment_required",
  "subscription_required",
  "label_issue",
  "invalid_address",
  "customs_information_required",
  "internal_review",
] as const

export type PublicApiOnHoldReason =
  (typeof PUBLIC_API_ON_HOLD_REASONS)[number]
